import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { CompetitionDetailTable } from 'pages/purchase-pages/competition-detail/CompetitionDetailTable'
import { PurchaseLayout } from 'hoc/PurchaseLayout'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTenderOverviewQuery } from 'services/tender'
import { formatTimeStampToDateWithTime } from 'hooks/date/formattedTime'
import { useTenderInstructionCurrentQuery } from 'services/tender/tenderInstructions'
import { useProcurementRequirementsQuery } from 'services/lot/procurement-requirements'
import { useQueryState } from 'react-router-use-location-state'
import { useTenderPassedDocDeadline } from 'hooks/date/useTenderPassedDocDeadline'
import { useTranslation } from 'react-i18next'

const breadCrumbs = [
  {
    id: 'home',
    title: 'Ana səhifə',
    path: '/',
  },
  {
    id: 'OPEN_TENDER',
    title: 'Açıq tender (e-tender)',
    path: '/competitions',
  },
  {
    title: ' ',
  },
]

export const CompetitionDetail: React.FC = () => {
  const isMobile = useMediaQuery('(max-width: 1200px)')
  const { id } = useParams()
  const [breadCrumbItems, setBreadCrumbItems] = useState(breadCrumbs)
  const rowsPerPage = 3
  const [page, setPage] = useQueryState('page', 0)
  const navigate = useNavigate()
  const { t } = useTranslation()

  const {
    data: tender,
    isLoading,
    isError,
  } = useTenderOverviewQuery({ id: id ?? '' })

  const hasDocDeadlinePassed = useTenderPassedDocDeadline(tender)

  const args = {
    page,
    size: rowsPerPage,
    lotId: tender?.lotId ?? '',
  } as const

  const { data: procurementRequirement } = useProcurementRequirementsQuery(args, {
    skip: !tender?.lotId,
  })

  const { data: tenderInstruction } = useTenderInstructionCurrentQuery()

  useEffect(() => {
    if (id && tender) {
      setBreadCrumbItems([
        {
          id: 'home',
          title: 'Ana səhifə',
          path: '/',
        },
        {
          id: 'OPEN_TENDER',
          title: 'Açıq tender (e-tender)',
          path: '/competitions',
        },
        {
          title: tender.name,
        },
      ])
    }
    return () => {
      setBreadCrumbItems(breadCrumbs)
    }
  }, [id, tender])

  const handleLogin = () => {
    navigate(`/portal/tender-participation/${id}`)
  }

  if (tender === undefined) {
    return <></>
  }

  return (
    <>
      <PurchaseLayout maxWidth={false} breadCrumbItems={breadCrumbItems}>
        <Container maxWidth="lg" disableGutters={isMobile ? false : true}>
          <Typography variant="h5">{tender.name}</Typography>
          <Typography variant="subtitle1">{t('general-info')}</Typography>
          <Box sx={{ paddingBottom: { xs: '20px', md: '50px' } }}>
            <Grid container rowSpacing="12px">
              <Grid item xs={6}>
                <Typography variant="h6">
                  {t('tender-organization-name')}:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6" sx={{ color: '#050f21' }}>
                  {tender.organizationName ?? '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6">
                  {t('tender-organization-address')}:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6" sx={{ color: '#050f21' }}>
                  {tender.organizationAddress ?? '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6">{t('tender-name')}:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6" sx={{ color: '#050f21' }}>
                  {tender.name ?? '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6">
                  {t('tender-published-date-time')}:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6" sx={{ color: '#050f21' }}>
                  {tender.startTime
                    ? formatTimeStampToDateWithTime(tender.startTime)
                    : '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6">
                  {t('submission-final-date-time')}:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6" sx={{ color: '#050f21' }}>
                  {tender.docSubmissionDeadline
                    ? formatTimeStampToDateWithTime(tender.docSubmissionDeadline)
                    : '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6">{t('envelope-open-date-time')}:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6" sx={{ color: '#050f21' }}>
                  {tender.envelopeOpeningTime
                    ? formatTimeStampToDateWithTime(tender.envelopeOpeningTime)
                    : '-'}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Container>
        {procurementRequirement && procurementRequirement.items.length > 0 && (
          <Container maxWidth={false} disableGutters={isMobile ? false : true}>
            <Box
              sx={{
                padding: { xs: '20px 0', md: '50px 0', background: '#fafbfb' },
              }}
            >
              <Container maxWidth="lg" disableGutters={isMobile ? false : true}>
                <CompetitionDetailTable
                  data={procurementRequirement}
                  lotId={tender?.lotId}
                />
              </Container>
            </Box>
          </Container>
        )}
        <Container maxWidth="lg" disableGutters={isMobile ? false : true}>
          <Box sx={{ padding: { xs: '20px 0', md: '50px 0' } }}>
            <Grid container columnSpacing="24px" rowGap={'20px'}>
              <Grid item md={12}>
                <Box
                  sx={{
                    background: '#fafbfb',
                    border: '1px solid #e1e4e8',
                    borderRadius: '4px',
                    padding: '16px',
                    height: '100%',
                  }}
                >
                  <Typography color="primary">
                    {t('participation-fee')}: {tender.participationFeeAmount ?? '-'}{' '}
                    AZN
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ color: '#283a5b', marginTop: '8px' }}
                  >
                    {t('participation-fee-info')}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
        <Container maxWidth={false} disableGutters={isMobile ? false : true}>
          <Box
            sx={{ padding: { xs: '20px 0', md: '50px 0', background: '#fafbfb' } }}
          >
            <Container maxWidth="lg" disableGutters={isMobile ? false : true}>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: '16px',
                  lineHeight: '24px',
                  color: '#050f21',
                  marginBottom: '12px',
                }}
              >
                {t('tender-org-members')}
              </Typography>
              <Grid container>
                <Grid item xs={12} md={3}>
                  <Box
                    sx={{
                      height: '40px',
                      background: '#fff',
                      padding: '12px 16px',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '20px',
                      color: '#050f21',
                      border: '1px solid #e1e4e8',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {tender.contactPeople[0]?.fullName ?? '-'}
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box
                    sx={{
                      height: '40px',
                      background: '#fff',
                      padding: '12px 16px',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '20px',
                      color: '#050f21',
                      border: '1px solid #e1e4e8',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {tender.contactPeople[0]?.position ?? '-'}
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box
                    sx={{
                      height: '40px',
                      background: '#fff',
                      padding: '12px 16px',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '20px',
                      border: '1px solid #e1e4e8',
                      display: 'flex',
                      alignItems: 'center',
                      color: 'rgb(3, 34, 76)',
                    }}
                  >
                    {tender.contactPeople[0]?.email ?? '-'}
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box
                    sx={{
                      height: '40px',
                      background: '#fff',
                      padding: '12px 16px',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '20px',
                      border: '1px solid #e1e4e8',
                      display: 'flex',
                      alignItems: 'center',
                      color: 'rgb(3, 34, 76)',
                    }}
                  >
                    {tender.contactPeople[0]?.phoneNumber ?? '-'}
                  </Box>
                </Grid>
                <Grid item xs={12} md={12}>
                  <Box
                    sx={{
                      height: '40px',
                      background: '#fff',
                      padding: '12px 16px',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '20px',
                      border: '1px solid #e1e4e8',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#050f21',
                      margin: '12px 0',
                    }}
                  >
                    {t('required-participants-count')}: 3
                  </Box>
                </Grid>
              </Grid>
            </Container>
          </Box>
        </Container>
        <Container maxWidth="lg" disableGutters={isMobile ? false : true}>
          <Box sx={{ paddingTop: { xs: '20px', md: '50px' } }}>
            <Box
              sx={{
                background: '#f7f8f9',
                border: '1px solid #e1e4e8',
                borderRadius: '4px',
                padding: '24px',
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 500, color: '#050f21', marginBottom: '24px' }}
              >
                {t('portal-note')}
              </Typography>
              <Grid container>
                <Grid item xs={12} md={9}>
                  <Box
                    sx={{
                      background: '#fff',
                      borderRadius: '8px',
                      padding: '8px',
                      textAlign: 'center',
                      marginRight: '24px',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 400,
                        color: '#283a5b',
                        lineHeight: '20px',
                      }}
                    >
                      {t('current-tender-terms')}{' '}
                      {tender.envelopeOpeningTime
                        ? formatTimeStampToDateWithTime(tender.envelopeOpeningTime)
                        : '-'}{' '}
                      {t('from-date-obtain')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    sx={{ marginTop: { xs: '16px', md: 0 } }}
                    fullWidth
                    variant="contained"
                    style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                    onClick={handleLogin}
                    disabled={hasDocDeadlinePassed}
                  >
                    {t('participate-in-tender')}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Container>
      </PurchaseLayout>
    </>
  )
}
